// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `event_type` value this module selects on. PLATFORM selector vocabulary
 * in the 64.153.0 tradition (`rights_request_*`, `authority_direction_response`,
 * `regulated_account_lifecycle`): packs declare it on deadline records;
 * nothing else in `src/` may branch on a pack's instrument, authority or
 * sla_type strings.
 */
export const SECTOR_LICENCE_LIFECYCLE_EVENT_TYPE = 'sector_licence_lifecycle';
/**
 * What a declared window counts from. A pack's deadline record names one of
 * these in `trigger_event`; the platform anchors the window on the licence's
 * `issued_at` or `expires_at` accordingly and reports `anchor_date_missing`
 * when the licence record does not carry that date.
 */
export const LICENCE_TRIGGER_EVENTS = ['licence_issued', 'licence_expiry'];
/**
 * Which way the window runs from its anchor. Absent on a pack record means
 * `after` (a filing due N days after issue); `before` models a renewal that
 * must be lodged N days before expiry.
 */
export const LICENCE_WINDOW_DIRECTIONS = ['after', 'before'];
/**
 * Platform selector on a deadline record: `gate_citation: true` marks the ONE
 * declaring record per kind (per pack) that the marketing-claim gate cites
 * when it refuses — the licensing provision, not a filing window that happens
 * to declare the same kind (gate E12.5 pass 1, P1-B1/C1: on the real pack the
 * gate cited the seven-working-day publication clause because it took
 * `declarations[0]`). A record so flagged is a precondition, not a period:
 * `obligations()` excludes it, so no licence carries a permanent `blocked`
 * row for a provision that has no clock (P2-C5). Where no record of a kind
 * carries the flag the gate cites the declaration with no numeric window,
 * else the first declaring record — never blindly the first.
 */
export const LICENCE_GATE_CITATION_FIELD = 'gate_citation';
/**
 * Tags on guidance records (`grc_regulatory_guidances`) that the screens read
 * to render the pack's own boundary statements (AC3, AC8). The platform
 * selects guidance by tag and renders the pack's text; it authors none of it
 * beyond the generic frame sentence.
 */
export const BOUNDARY_GUIDANCE_TAGS = {
    /** What the platform is not: not a licensed venue, not a statutory service, not a guarantee. */
    platformBoundary: 'platform_boundary',
    /** Who is and is not in scope, and any pilot-linked duration as the pack states it. */
    scopeBoundary: 'scope_boundary',
    /** No verification mechanism is treated as automatically satisfying the duty. */
    investorVerification: 'investor_verification_boundary',
    /** The prohibition family over account data, rendered beside a justification. */
    accountData: 'account_data_boundary',
};
// ── Sector licences (Slice A) ──
/**
 * The organisation's own account of where a licence stands. `not_applied`
 * is a deliberate first state: an organisation may record a kind it has been
 * told it needs before it has applied, so the register can carry the gap.
 */
export const SECTOR_LICENCE_STATUSES = [
    'not_applied',
    'pending',
    'active',
    'suspended',
    'revoked',
    'expired',
];
export const SECTOR_LICENCE_AUTHORITY_SOURCES = ['pack', 'operator'];
/** One installed deadline record offering a window (or a bare reference) for a licence kind. */
/**
 * Who a declared window binds — Sprint 62.24.0. A pack row may declare a
 * period the AUTHORITY owes (a decision within N working days of a dossier),
 * and the register carries it as the authority's stated period rather than
 * dropping it or computing an organisation due date from it.
 */
export const LICENCE_WINDOW_OWNERS = ['organization', 'authority'];
// ── Marketing-claim approvals and the gate (Slice B) ──
export const MARKETING_APPROVAL_STATUSES = ['pending', 'approved', 'withdrawn'];
// ── Collections ──
export const SECTOR_LICENCES_COLLECTION = 'grc_sector_licences';
export const MARKETING_CLAIM_APPROVALS_COLLECTION = 'grc_marketing_claim_approvals';
// ── Error codes ──
export const SECTOR_LICENSING_ERROR_CODES = {
    LICENCE_NOT_FOUND: 'SECTOR_LICENCE_NOT_FOUND',
    /** HTTP 409 — the gate refused; the refusal is recorded on the approval. */
    MARKETING_GATE_REFUSED: 'MARKETING_GATE_REFUSED',
    MARKETING_APPROVAL_NOT_FOUND: 'MARKETING_APPROVAL_NOT_FOUND',
    /** Approve/withdraw on a record that is no longer `pending`. */
    MARKETING_APPROVAL_NOT_PENDING: 'MARKETING_APPROVAL_NOT_PENDING',
};
//# sourceMappingURL=sector-licensing.js.map